import React from "react";
import { cn } from "@/lib/utils";

/**
 * Small pill labels. Two families:
 *   variant  — the semantic families (primary/violet/neutral/success/warning/danger)
 *   status   — the four capability-status badges used on /platform/tokenization
 * Pass `onDark` on dark surfaces for the light-family variants to switch to
 * translucent-dark equivalents so they read on --surface-base.
 */

const LIGHT_VARIANT = {
  primary: "bg-[#EEE8F5] text-[#2A0A4A]",
  violet: "bg-[#F0E4FD] text-[#7200DB]",
  neutral: "bg-[#DDE2E9] text-[#4A4D57]",
  success: "bg-[#E3F2E8] text-[#1E6B3C]",
  warning: "bg-[#FBF0DC] text-[#8A5A00]",
  danger: "bg-[#FBE3E3] text-[#A11C1C]",
};

const DARK_VARIANT = {
  primary: "bg-[rgba(114,0,219,0.14)] text-[#C9B8DD]",
  violet: "bg-[rgba(114,0,219,0.14)] text-[#EDF1F5]",
  neutral: "bg-[rgba(237,241,245,0.08)] text-[#DDE2E9]",
  success: "bg-[rgba(46,160,89,0.16)] text-[#7CDCA0]",
  warning: "bg-[rgba(220,150,0,0.14)] text-[#F5C67B]",
  danger: "bg-[rgba(220,60,60,0.14)] text-[#F0A3A3]",
};

const STATUS = {
  LIVE: {
    label: "Live",
    className: "bg-[#E3F2E8] text-[#1E6B3C]",
    darkClassName: "bg-[rgba(46,160,89,0.16)] text-[#7CDCA0]",
    dot: true,
    dotClassName: "bg-[#1E6B3C]",
  },
  IN_PARTNERSHIP: {
    label: "In partnership",
    className: "bg-[#F0E4FD] text-[#7200DB]",
    darkClassName: "bg-[rgba(114,0,219,0.14)] text-[#C9B8DD]",
  },
  PILOT: {
    label: "Pilot",
    className: "bg-[#DDE2E9] text-[#4A4D57]",
    darkClassName: "bg-[rgba(237,241,245,0.08)] text-[#DDE2E9]",
  },
  ROADMAP: {
    label: "Roadmap",
    className: "bg-transparent text-[#7D818C] border border-[#4A4D57]",
    darkClassName:
      "bg-transparent text-[#7D818C] border border-[rgba(237,241,245,0.24)]",
  },
};

export function Badge({
  variant = "neutral",
  status,
  onDark = false,
  className,
  children,
}) {
  if (status && STATUS[status]) {
    const s = STATUS[status];
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-[10px] py-[3px] text-[12px] font-semibold tracking-[0.02em]",
          onDark ? s.darkClassName : s.className,
          className,
        )}
        data-testid={`badge-status-${status}`}
      >
        {s.dot && (
          <span
            className={cn(
              "h-1.5 w-1.5 rounded-full",
              onDark ? "bg-[#7CDCA0]" : s.dotClassName,
            )}
            aria-hidden
          />
        )}
        {children ?? s.label}
      </span>
    );
  }

  const map = onDark ? DARK_VARIANT : LIGHT_VARIANT;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-[10px] py-[3px] text-[12px] font-semibold tracking-[0.02em]",
        map[variant],
        className,
      )}
      data-testid={`badge-${variant}`}
    >
      {children}
    </span>
  );
}

export default Badge;
